// Operator login. Two steps in one page so a browser back-button
// doesn't lose challenge state:
//
//   1. Password — POST /admin/login. 200 → mint cookie now, 428 →
//      branch into MFA.
//   2. TOTP    — POST /admin/login/mfa. 200 → mint cookie.
//
// On success we seed `keys.me()` directly from the login response.
// Without this, _app.tsx's beforeLoad re-uses the cached unauth
// `null` (TanStack Query's ensureQueryData returns existing data
// even when invalidated), bounces back to /login, and the user
// appears to be stuck on the login page after submitting valid
// credentials. The route also reads `?redirect=…` so a deep link
// into a protected page returns the user to where they tried to go.
import {
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { login, completeMFA, fetchMeOrNull } from "@/lib/auth";
import { humanizeError, humanizeMFAError } from "@/lib/errorMessages";
import { keys } from "@/lib/queries";
import { redirectTarget, sanitizeRedirect } from "@/lib/redirect";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SparkHorizontal } from "@/components/ui/SparkHorizontal";
import { LanguageSelector } from "@/components/i18n/LanguageSelector";
import { useTranslation } from "react-i18next";
import type {
  AdminLoginResponse,
  AdminLoginMFAChallenge,
  AdminLoginFactor,
  AdminMe,
} from "@/lib/types";

interface LoginSearch {
  redirect?: string;
}

export const Route = createFileRoute("/login")({
  // Narrow ?redirect= to a same-origin path at the boundary, so nothing
  // downstream has to remember that this value is untrusted.
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: sanitizeRedirect(search.redirect) ?? undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    // If a session is already live, bounce to the dashboard (or back
    // to whatever protected page kicked us here).
    const me = await context.queryClient.ensureQueryData({
      queryKey: keys.me(),
      queryFn: fetchMeOrNull,
      staleTime: 5_000,
    });
    if (me) throw redirect({ to: redirectTarget(search.redirect) });
  },
  component: LoginPage,
});

type Step =
  | { kind: "password" }
  | {
      kind: "mfa";
      challenge: AdminLoginMFAChallenge;
      factor: AdminLoginFactor;
    };

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>({ kind: "password" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [err, setErr] = useState<string | null>(null);

  // Focus the first OTP box when we transition into the MFA step.
  useEffect(() => {
    if (step.kind === "mfa") otpRefs.current[0]?.focus();
  }, [step.kind]);

  const goAfterAuth = (res: AdminLoginResponse) => {
    // Seed the auth cache from the login response so _app.tsx's
    // beforeLoad sees the fresh admin immediately. invalidateQueries
    // alone wouldn't refetch (no observers on /login).
    const me: AdminMe = {
      user_id: res.user_id,
      email: res.email,
      role: res.role,
      csrf_token: res.csrf_token,
      expires_at: res.expires_at,
    };
    qc.setQueryData(keys.me(), me);
    navigate({ to: redirectTarget(search.redirect) });
  };

  const passwordMut = useMutation({
    mutationFn: () => login({ email, password }),
    onSuccess: (res) => {
      setErr(null);
      if (res.kind === "needs_mfa") {
        const factor = res.challenge.factors[0];
        if (!factor) {
          setErr(
            t("auth.missingFactor"),
          );
          return;
        }
        setStep({ kind: "mfa", challenge: res.challenge, factor });
        return;
      }
      goAfterAuth(res.data);
    },
    onError: (e) => setErr(humanizeError(e)),
  });

  const mfaMut = useMutation({
    mutationFn: () => {
      if (step.kind !== "mfa") throw new Error(t("auth.notInMfa"));
      return completeMFA({
        challenge_id: step.challenge.challenge_id,
        factor_id: step.factor.factor_id,
        code: otp.join(""),
      });
    },
    onSuccess: (res) => {
      setErr(null);
      goAfterAuth(res);
    },
    onError: (e) =>
      setErr(
        // Pass the selected factor's kind so a wrong code (BACKOFFICE_000211,
        // identical across methods) renders method-specific copy.
        humanizeMFAError(e, step.kind === "mfa" ? step.factor.kind : "TOTP"),
      ),
  });

  function setOtpAt(i: number, value: string) {
    // Pasted multi-digit input: distribute starting at box i. Single
    // typed digits behave the same as before — drop into the focused
    // box and advance the cursor.
    const digits = value.replace(/\D/g, "");
    if (!digits) {
      const next = otp.slice();
      next[i] = "";
      setOtp(next);
      return;
    }
    const next = otp.slice();
    let cursor = i;
    for (const d of digits) {
      if (cursor > 5) break;
      next[cursor] = d;
      cursor++;
    }
    setOtp(next);
    const focusIdx = Math.min(cursor, 5);
    otpRefs.current[focusIdx]?.focus();
    // Auto-submit on the last digit so the operator can paste a 6-digit
    // code from their authenticator and never reach for the mouse.
    if (cursor >= 6 && next.every((d) => d !== "")) {
      mfaMut.mutate();
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <SparkHorizontal viewBox="243 632 1611 628" className="h-8 w-auto shrink-0 text-t-1" />
            <div className="text-2xs uppercase tracking-[0.18em] text-t-3">{t("common.backoffice")}</div>
          </div>
          <LanguageSelector />
        </div>
        <div className="modal">
          <div className="modal-glow" />
          <div className="modal-body">
            {step.kind === "password" ? (
              <PasswordStep
                email={email}
                password={password}
                err={err}
                pending={passwordMut.isPending}
                onEmail={setEmail}
                onPassword={setPassword}
                onSubmit={() => passwordMut.mutate()}
              />
            ) : (
              <MFAStep
                challengeEmail={step.challenge.email}
                factorName={step.factor.friendly_name}
                factorKind={step.factor.kind}
                otp={otp}
                otpRefs={otpRefs}
                err={err}
                pending={mfaMut.isPending}
                onOtpAt={setOtpAt}
                onSubmit={() => mfaMut.mutate()}
                onBack={() => {
                  setStep({ kind: "password" });
                  setOtp(["", "", "", "", "", ""]);
                  setErr(null);
                }}
              />
            )}
          </div>
        </div>
        <p className="mt-5 text-center text-2xs uppercase tracking-[0.18em] text-t-4">
          {t("auth.securitySummary")}
        </p>
      </div>
    </div>
  );
}

function PasswordStep({
  email,
  password,
  err,
  pending,
  onEmail,
  onPassword,
  onSubmit,
}: {
  email: string;
  password: string;
  err: string | null;
  pending: boolean;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="auth-eyebrow">{t("auth.operatorConsole")}</div>
      <h2 className="auth-headline">{t("auth.signIn")}</h2>
      <p className="auth-sub">{t("auth.subtitle")}</p>
      {err && (
        <div className="banner danger" role="alert">
          <span aria-hidden>⚠</span>
          <span>{err}</span>
        </div>
      )}
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Input
          label={t("auth.email")}
          type="email"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          autoComplete="email"
          autoFocus
          required
          spellCheck={false}
        />
        <Input
          label={t("auth.password")}
          type="password"
          value={password}
          onChange={(e) => onPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <Button
          type="submit"
          loading={pending}
          className="btn-block btn-lg mt-1"
        >
          {pending ? t("auth.signingIn") : t("auth.continue")}
        </Button>
      </form>
      <p className="auth-foot">
        {t("auth.lostAccess")}{" "}
        <a href="mailto:ops@smartmarket.local">{t("auth.contactAdmin")}</a>
      </p>
    </>
  );
}

function MFAStep({
  challengeEmail,
  factorName,
  factorKind,
  otp,
  otpRefs,
  err,
  pending,
  onOtpAt,
  onSubmit,
  onBack,
}: {
  challengeEmail: string;
  factorName: string;
  factorKind: string;
  otp: string[];
  otpRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  err: string | null;
  pending: boolean;
  onOtpAt: (i: number, v: string) => void;
  onSubmit: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const isEmail = factorKind === "EMAIL_OTP";
  return (
    <>
      <div className="auth-eyebrow flex items-center gap-1.5">
        <ShieldIcon />
        <span>{t("auth.twoFactor")}</span>
      </div>
      <h2 className="auth-headline">{t("auth.enterCode")}</h2>
      <p className="auth-sub">
        {isEmail
          ? t("auth.emailCode", { email: challengeEmail })
          : t("auth.authenticatorCode", { factor: factorName, email: challengeEmail })}
      </p>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className="otp">
          {otp.map((v, i) => (
            <input
              key={i}
              ref={(el) => {
                otpRefs.current[i] = el;
              }}
              maxLength={6 /* allow paste, distribution handled in onChange */}
              inputMode="numeric"
              pattern="[0-9]*"
              value={v}
              onChange={(e) => onOtpAt(i, e.target.value)}
              onPaste={(e) => {
                const txt = e.clipboardData.getData("text");
                if (/\d/.test(txt)) {
                  e.preventDefault();
                  onOtpAt(i, txt);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !v && i > 0)
                  otpRefs.current[i - 1]?.focus();
                if (e.key === "ArrowLeft" && i > 0)
                  otpRefs.current[i - 1]?.focus();
                if (e.key === "ArrowRight" && i < 5)
                  otpRefs.current[i + 1]?.focus();
              }}
              autoComplete="one-time-code"
              className={v ? "filled" : undefined}
              aria-label={t("auth.digit", { digit: i + 1 })}
            />
          ))}
        </div>
        {err && (
          <div className="banner danger" role="alert">
            <span aria-hidden>⚠</span>
            <span>{err}</span>
          </div>
        )}
        <Button
          type="submit"
          loading={pending}
          disabled={otp.some((d) => !d)}
          className="btn-block btn-lg"
        >
          {pending ? t("auth.verifying") : t("auth.verifyAndSignIn")}
        </Button>
        <button
          type="button"
          onClick={onBack}
          className="block w-full text-center text-2xs text-t-3 transition-colors hover:text-t-1"
        >
          {t("auth.backToPassword")}
        </button>
      </form>
      <p className="auth-foot">
        {t("auth.lostAuthenticator")}{" "}
        <a href="mailto:ops@smartmarket.local">
          {t("auth.reenroll")}
        </a>
      </p>
    </>
  );
}

function ShieldIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
